import React from "react";
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ReplayIcon from "@mui/icons-material/Replay";
import HistoryIcon from "@mui/icons-material/History";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import BoltIcon from "@mui/icons-material/Bolt";
import {
  APPROVAL_STATUS,
  PUSH_STATUS,
  RENDER_STATUS,
  SentContentTooltip,
  StatusChip,
  TimeAgo,
  VIDEO_SOURCE_META,
  getRowState,
} from "./constants";

const HEAD_SX = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: 0.7,
  textTransform: "uppercase",
  color: "#64748b",
  bgcolor: "#f8fafc",
  borderBottom: "1px solid #e6ebf1",
  py: 1.1,
  whiteSpace: "nowrap",
};

/** Dense view — for scanning a lot of pushes rather than judging one video. */
export default function ApprovalsTable({ rows, isAdmin, busyId, actions }) {
  return (
    <TableContainer
      sx={{ border: "1px solid #e6ebf1", borderRadius: 3, overflow: "auto", maxHeight: "calc(100vh - 320px)" }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD_SX, width: 84 }} />
            <TableCell sx={HEAD_SX}>Video</TableCell>
            <TableCell sx={HEAD_SX}>Target Feed</TableCell>
            <TableCell sx={HEAD_SX}>Source</TableCell>
            <TableCell sx={HEAD_SX}>Created</TableCell>
            <TableCell sx={HEAD_SX}>Approval</TableCell>
            <TableCell sx={HEAD_SX}>Push Status</TableCell>
            <TableCell sx={{ ...HEAD_SX, textAlign: "right" }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const s = getRowState(row, isAdmin);
            const busy = busyId === row.id;
            const accent = APPROVAL_STATUS[row.approval_status]?.color || "#94a3b8";
            const source = VIDEO_SOURCE_META[row.video_source] || VIDEO_SOURCE_META.ORIGINAL;

            return (
              <TableRow
                key={row.id}
                hover
                sx={{
                  "& td": { borderBottom: "1px solid #f1f5f9", py: 1 },
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                <TableCell sx={{ borderLeft: `3px solid ${accent}` }}>
                  <Box
                    onClick={() => s.canPreview && actions.onPreview(row)}
                    sx={{
                      position: "relative",
                      width: 64,
                      height: 40,
                      borderRadius: 1.5,
                      overflow: "hidden",
                      bgcolor: "#0b1120",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: s.canPreview ? "pointer" : "default",
                      "&:hover .ap-t-play": { opacity: 1 },
                    }}
                  >
                    {row.thumbnail_url ? (
                      <Box
                        component="img"
                        src={row.thumbnail_url}
                        alt=""
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <ImageNotSupportedIcon sx={{ fontSize: 16, color: "#334155" }} />
                    )}
                    {s.canPreview && (
                      <Box
                        className="ap-t-play"
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(2,6,23,.45)",
                          opacity: 0,
                          transition: "opacity .15s",
                        }}
                      >
                        <PlayArrowIcon sx={{ fontSize: 18, color: "#fff" }} />
                      </Box>
                    )}
                  </Box>
                </TableCell>

                <TableCell sx={{ maxWidth: 260 }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title={<SentContentTooltip s={s} />} placement="top-start">
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {s.title}
                      </Typography>
                    </Tooltip>
                    {row.is_force_push && (
                      <Tooltip title="Force push — bypasses Admin Portal's queue">
                        <BoltIcon sx={{ fontSize: 14, color: "#d97706", flexShrink: 0 }} />
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: "monospace", fontSize: 10.5 }}>
                    {s.key}
                  </Typography>
                </TableCell>

                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography variant="body2" noWrap>
                    {s.target}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {row.admin_portal_group_label} ({row.admin_portal_group_type})
                  </Typography>
                </TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    label={source.label}
                    sx={{
                      height: 20,
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.3,
                      color: source.color,
                      bgcolor: source.bg,
                      "& .MuiChip-label": { px: 0.8 },
                    }}
                  />
                  {s.isEdited && row.render_status && (
                    <Box mt={0.5}>
                      <StatusChip map={RENDER_STATUS} value={row.render_status} />
                    </Box>
                  )}
                  {row.video_source === "ORIGINAL" && (
                    <Tooltip title="No explicit URL is sent for Original — Admin Portal's own video_url_type setting on the target feed picks the actual rendition. This preview is CreatorVault's best guess, not a guarantee of what's published.">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ cursor: "help", fontSize: 10.5 }}
                      >
                        Portal picks rendition
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell>
                  <TimeAgo iso={row.created_date} stale={s.isStale} variant="body2" sx={{ display: "block" }} />
                  {row.pushed_by && (
                    <Typography variant="caption" color="text.disabled" noWrap>
                      by {row.pushed_by}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <StatusChip map={APPROVAL_STATUS} value={row.approval_status} />
                  {row.rejection_reason && (
                    <Tooltip title={row.rejection_reason}>
                      <Typography
                        variant="caption"
                        color="error.main"
                        display="block"
                        noWrap
                        sx={{ maxWidth: 180, cursor: "help" }}
                      >
                        {row.rejection_reason}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell>
                  <StatusChip map={PUSH_STATUS} value={row.status} />
                  {row.error_message && (
                    <Tooltip title={row.error_message}>
                      <Typography
                        variant="caption"
                        color="error.main"
                        display="block"
                        noWrap
                        sx={{ maxWidth: 200, cursor: "help" }}
                      >
                        {row.error_message}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip
                    title={
                      row.preview_url
                        ? "Preview video"
                        : s.renderBlocksApproval
                        ? s.renderFailed
                          ? "Render failed — open to see details"
                          : "Render still in progress — open to see status"
                        : row.thumbnail_url
                        ? "No video preview — showing thumbnail only"
                        : "No preview available"
                    }
                  >
                    <span>
                      <IconButton size="small" disabled={!s.canPreview} onClick={() => actions.onPreview(row)}>
                        <PlayArrowIcon fontSize="small" color={s.canPreview ? "primary" : "disabled"} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {isAdmin && s.isPending && (
                    <>
                      <Tooltip title={s.approveDisabledReason}>
                        <span>
                          <IconButton size="small" disabled={busy || !s.canApprove} onClick={() => actions.onApprove(row.id)}>
                            <ThumbUpIcon fontSize="small" sx={{ color: s.canApprove ? "#10b981" : undefined }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <span>
                          <IconButton size="small" disabled={busy} onClick={() => actions.onReject(row.id)}>
                            <ThumbDownIcon fontSize="small" sx={{ color: "#ef4444" }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                  {s.canRetry && (
                    <Tooltip title={row.error_message ? `Retry: ${row.error_message}` : "Retry — will be attempted again on the next scheduler tick"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={busy}
                          onClick={() => {
                            // See ApprovalCard.js — Admin Portal's duplicate-push
                            // guard (409) needs allow_duplicate set to retry past it.
                            const isDuplicate = /already (in|queued for)/i.test(row.error_message || "");
                            if (isDuplicate && !window.confirm(
                              `${row.error_message}\n\nRetry anyway and push it again as a duplicate?`
                            )) {
                              return;
                            }
                            actions.onRetry(row.id, isDuplicate);
                          }}
                        >
                          <ReplayIcon fontSize="small" sx={{ color: "#f59e0b" }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="View full logs — every step this push has been through">
                    <IconButton size="small" onClick={() => actions.onLogs(row)}>
                      <HistoryIcon fontSize="small" sx={{ color: "#64748b" }} />
                    </IconButton>
                  </Tooltip>
                  {s.canDelete && (
                    <Tooltip title="Delete this push and any files it still holds (S3 + local)">
                      <span>
                        <IconButton size="small" disabled={busy} onClick={() => actions.onDelete(row)}>
                          <DeleteForeverIcon fontSize="small" sx={{ color: "#dc2626" }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
